import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Map, Library, Music, Leaf } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [treeCount, setTreeCount] = useState(0);
  const [coverPhoto, setCoverPhoto] = useState('https://images.unsplash.com/photo-1603283084627-26588b005761?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxBdXN0cmFsaWFuJTIwZXVjYWx5cHR1cyUyMHRyZWUlMjBuYXR1cmV8ZW58MHx8fHwxNzY1NTk0Mjg2fDA&ixlib=rb-4.1.0&q=85');
  const [heroLogoUrl, setHeroLogoUrl] = useState(null);
  
  // Hero text state
  const [heroTitle, setHeroTitle] = useState('TreeMuseum');
  const [heroSubtitle, setHeroSubtitle] = useState('Experience Australian trees through multiple perspectives—indigenous wisdom, scientific knowledge, horticultural expertise, logging history, and poetic beauty.');
  const [button1Text, setButton1Text] = useState('Explore Map');
  const [button2Text, setButton2Text] = useState('View Gallery');
  
  // Features section state
  const [featuresTitle, setFeaturesTitle] = useState('Discover Trees Through Sound & Story');
  const [featuresSubtitle, setFeaturesSubtitle] = useState('Every tree tells multiple stories. Listen to diverse voices and perspectives.');
  
  // Homepage box content state
  const [box1Title, setBox1Title] = useState('Multiple Audio Tracks');
  const [box1Text, setBox1Text] = useState('Listen to indigenous, scientific, logger, horticultural, and poetic perspectives—all telling unique stories about each tree.');
  const [box2Title, setBox2Title] = useState('Interactive Map');
  const [box2Text, setBox2Text] = useState('Explore the geographic distribution of Australian trees with precise geo-location data on an interactive map.');
  const [box3Title, setBox3Title] = useState('Rich Information');
  const [box3Text, setBox3Text] = useState('Detailed scientific data, habitat information, conservation status, and cultural significance for each tree species.');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [treesRes, settingsRes] = await Promise.all([
          axios.get(`${API}/trees`),
          axios.get(`${API}/settings`)
        ]);
        setTreeCount(treesRes.data.length);
        
        const settings = settingsRes.data;
        if (settings.cover_photo_url) {
          setCoverPhoto(settings.cover_photo_url);
        }
        if (settings.hero_logo_url) {
          setHeroLogoUrl(settings.hero_logo_url);
        }
        
        // Load hero text
        if (settings.homepage_hero_title) setHeroTitle(settings.homepage_hero_title);
        if (settings.homepage_hero_subtitle) setHeroSubtitle(settings.homepage_hero_subtitle);
        if (settings.homepage_button1_text) setButton1Text(settings.homepage_button1_text);
        if (settings.homepage_button2_text) setButton2Text(settings.homepage_button2_text);
        
        // Load features section text
        if (settings.homepage_features_title) setFeaturesTitle(settings.homepage_features_title);
        if (settings.homepage_features_subtitle) setFeaturesSubtitle(settings.homepage_features_subtitle);
        
        // Load homepage box content
        if (settings.homepage_box1_title) setBox1Title(settings.homepage_box1_title);
        if (settings.homepage_box1_text) setBox1Text(settings.homepage_box1_text);
        if (settings.homepage_box2_title) setBox2Title(settings.homepage_box2_title);
        if (settings.homepage_box2_text) setBox2Text(settings.homepage_box2_text);
        if (settings.homepage_box3_title) setBox3Title(settings.homepage_box3_title);
        if (settings.homepage_box3_text) setBox3Text(settings.homepage_box3_text);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[70vh] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={coverPhoto}
            alt="Australian trees"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/40 via-primary/60 to-primary/80" />
        </div>
        
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center items-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-bold text-white mb-6 tracking-tight" data-testid="hero-title">
              {heroTitle}
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl leading-relaxed">
              {heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/map"
                className="inline-flex items-center gap-2 px-8 py-3 bg-accent text-foreground rounded-lg font-medium hover:bg-accent/90 transition-all hover:shadow-lg active:scale-95"
                data-testid="explore-map-btn"
              >
                <Map className="w-5 h-5" />
                {button1Text}
              </Link>
              <Link
                to="/gallery"
                className="inline-flex items-center gap-2 px-8 py-3 bg-white/10 backdrop-blur-sm text-white rounded-lg font-medium hover:bg-white/20 transition-all border border-white/30 active:scale-95"
                data-testid="view-gallery-btn"
              >
                <Library className="w-5 h-5" />
                {button2Text}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-primary mb-4" data-testid="features-title">
              {featuresTitle}
            </h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto">
              {featuresSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-8 border border-border rounded-lg bg-white hover:border-primary/50 transition-all"
              data-testid="feature-audio"
            >
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
                <Music className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-primary mb-3">{box1Title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {box1Text}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="p-8 border border-border rounded-lg bg-white hover:border-primary/50 transition-all"
              data-testid="feature-map"
            >
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
                <Map className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-primary mb-3">{box2Title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {box2Text}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="p-8 border border-border rounded-lg bg-white hover:border-primary/50 transition-all"
              data-testid="feature-science"
            >
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
                <Leaf className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-primary mb-3">{box3Title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {box3Text}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white/50 border-y border-border">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-block">
            <div className="text-5xl font-heading font-bold text-primary mb-2" data-testid="tree-count">
              {treeCount}
            </div>
            <div className="text-sm text-muted-foreground uppercase tracking-wider">
              Australian Trees Documented
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;